/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Layers, Plus, Edit, Trash2, Check, X, RefreshCw,
  DollarSign, TrendingUp, Users, Percent, BarChart3,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  ToggleLeft, ToggleRight
} from 'lucide-react';
import {
  usePricingTiers,
  useCreatePricingTier,
  useUpdatePricingTier,
  useActivatePricingTier,
  useDeactivatePricingTier,
  useDeletePricingTier,
  useModuleCodes,
  useVolumeDiscounts,
  useCreateVolumeDiscount,
  useUpdateVolumeDiscount,
  useDeleteVolumeDiscount,
  useSubscriptionAnalytics,
  useTotalMrr,
} from '@/hooks/api/useModulePricing';
import {
  ModulePricingTier,
  VolumeDiscountRule,
  CreateModulePricingTierRequest,
  UpdateModulePricingTierRequest,
  CreateVolumeDiscountRequest,
  PricingModel,
} from '@/types/superadmin.types';
import { Button } from '@/components/ui/Button';
import { FormInput } from '@/components/ui/FormInput';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { useNotification } from '@/hooks/useNotification';
import { isApiError, formatErrorForDisplay } from '@/services/api/errors';

// ============================================================================
// Skeleton Components
// ============================================================================

const TableRowSkeleton: React.FC<{ cols: number }> = ({ cols }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 bg-muted/20 rounded w-full"></div>
      </td>
    ))}
  </tr>
);

const StatCardSkeleton: React.FC = () => (
  <div className="bg-surface border border-border-subtle rounded-xl p-4 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-muted/20"></div>
      <div className="flex-1">
        <div className="h-3 w-20 bg-muted/20 rounded mb-2"></div>
        <div className="h-6 w-24 bg-muted/20 rounded"></div>
      </div>
    </div>
  </div>
);

// ============================================================================
// Pricing Tiers Tab
// ============================================================================

interface TierFormData {
  moduleCode: string;
  tierCode: string;
  tierName: string;
  description: string;
  pricingModel: PricingModel;
  monthlyPriceUsd: number;
  annualPriceUsd: number;
  perUserMonthlyUsd: number;
  perUserAnnualUsd: number;
  minUsers: number;
  maxUsers: number | null;
  maxStorageGb: number | null;
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  trialDays: number;
}

const defaultTierForm: TierFormData = {
  moduleCode: '',
  tierCode: '',
  tierName: '',
  description: '',
  pricingModel: 'FLAT',
  monthlyPriceUsd: 0,
  annualPriceUsd: 0,
  perUserMonthlyUsd: 0,
  perUserAnnualUsd: 0,
  minUsers: 1,
  maxUsers: null,
  maxStorageGb: null,
  isActive: true,
  isPublic: true,
  sortOrder: 0,
  trialDays: 14,
};

// Standard tier codes available for selection
const TIER_CODES = ['BASIC', 'PRO', 'ENTERPRISE'] as const;

const PricingTiersTab: React.FC = () => {
  const { t } = useTranslation('platform');
  const { success: showSuccess, error: showError } = useNotification();

  const { data: tiers = [], isLoading, refetch } = usePricingTiers();
  const { data: moduleCodes = [] } = useModuleCodes();
  const createMutation = useCreatePricingTier();
  const updateMutation = useUpdatePricingTier();
  const activateMutation = useActivatePricingTier();
  const deactivateMutation = useDeactivatePricingTier();
  const deleteMutation = useDeletePricingTier();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<ModulePricingTier | null>(null);
  const [formData, setFormData] = useState<TierFormData>(defaultTierForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterModule, setFilterModule] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;

  // Get unique module codes for filter
  const moduleOptions = useMemo(() => {
    const codes = new Set(tiers.map(t => t.moduleCode));
    return Array.from(codes).sort();
  }, [tiers]);

  // Filter tiers
  const filteredTiers = useMemo(() => {
    if (!filterModule) return tiers;
    return tiers.filter(t => t.moduleCode === filterModule);
  }, [tiers, filterModule]);

  // Pagination calculations
  const totalElements = filteredTiers.length;
  const totalPages = Math.ceil(totalElements / pageSize);

  // Paginated tiers
  const paginatedTiers = useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredTiers.slice(start, end);
  }, [filteredTiers, currentPage, pageSize]);

  // Group paginated tiers by module
  const groupedTiers = useMemo(() => {
    const groups: Record<string, ModulePricingTier[]> = {};
    paginatedTiers.forEach(tier => {
      if (!groups[tier.moduleCode]) {
        groups[tier.moduleCode] = [];
      }
      groups[tier.moduleCode].push(tier);
    });
    // Sort tiers within each group
    Object.values(groups).forEach(group => {
      group.sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return groups;
  }, [paginatedTiers]);

  // Reset page when filter changes
  const handleFilterChange = (value: string) => {
    setFilterModule(value);
    setCurrentPage(0);
  };

  const openCreateForm = () => {
    setFormData(defaultTierForm);
    setEditingTier(null);
    setIsFormOpen(true);
  };

  const openEditForm = (tier: ModulePricingTier) => {
    setFormData({
      moduleCode: tier.moduleCode,
      tierCode: tier.tierCode,
      tierName: tier.tierName,
      description: tier.description || '',
      pricingModel: tier.pricingModel,
      monthlyPriceUsd: tier.monthlyPriceUsd,
      annualPriceUsd: tier.annualPriceUsd,
      perUserMonthlyUsd: tier.perUserMonthlyUsd,
      perUserAnnualUsd: tier.perUserAnnualUsd,
      minUsers: tier.minUsers,
      maxUsers: tier.maxUsers || null,
      maxStorageGb: tier.maxStorageGb || null,
      isActive: tier.isActive,
      isPublic: tier.isPublic,
      sortOrder: tier.sortOrder,
      trialDays: tier.trialDays,
    });
    setEditingTier(tier);
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTier) {
        const updateData: UpdateModulePricingTierRequest = {
          tierName: formData.tierName,
          description: formData.description || undefined,
          monthlyPriceUsd: formData.monthlyPriceUsd,
          annualPriceUsd: formData.annualPriceUsd,
          perUserMonthlyUsd: formData.perUserMonthlyUsd,
          perUserAnnualUsd: formData.perUserAnnualUsd,
          minUsers: formData.minUsers,
          maxUsers: formData.maxUsers || undefined,
          maxStorageGb: formData.maxStorageGb || undefined,
          isActive: formData.isActive,
          isPublic: formData.isPublic,
          sortOrder: formData.sortOrder,
          trialDays: formData.trialDays,
        };
        await updateMutation.mutateAsync({ id: editingTier.id, data: updateData });
        showSuccess(t('modulePricing.tiers.messages.updated'));
      } else {
        const createData: CreateModulePricingTierRequest = {
          moduleCode: formData.moduleCode,
          tierCode: formData.tierCode,
          tierName: formData.tierName,
          description: formData.description || undefined,
          pricingModel: formData.pricingModel,
          monthlyPriceUsd: formData.monthlyPriceUsd,
          annualPriceUsd: formData.annualPriceUsd,
          perUserMonthlyUsd: formData.perUserMonthlyUsd,
          perUserAnnualUsd: formData.perUserAnnualUsd,
          minUsers: formData.minUsers,
          maxUsers: formData.maxUsers || undefined,
          maxStorageGb: formData.maxStorageGb || undefined,
          isActive: formData.isActive,
          isPublic: formData.isPublic,
          sortOrder: formData.sortOrder,
          trialDays: formData.trialDays,
        };
        await createMutation.mutateAsync(createData);
        showSuccess(t('modulePricing.tiers.messages.created'));
      }
      setIsFormOpen(false);
      setEditingTier(null);
    } catch (err) {
      const fallback = editingTier ? 'Failed to update tier' : 'Failed to create tier';
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: fallback };
      showError(message);
    }
  };

  const handleToggleActive = async (tier: ModulePricingTier) => {
    try {
      if (tier.isActive) {
        await deactivateMutation.mutateAsync(tier.id);
        showSuccess(t('modulePricing.tiers.messages.deactivated'));
      } else {
        await activateMutation.mutateAsync(tier.id);
        showSuccess(t('modulePricing.tiers.messages.activated'));
      }
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: 'Failed to update tier status' };
      showError(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      showSuccess(t('modulePricing.tiers.messages.deleted'));
      setDeleteConfirm(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: 'Failed to delete tier' };
      showError(message);
    }
  };

  const getModuleName = (code: string) => t(`modulePricing.modules.${code}`, code);
  const getTierName = (code: string) => t(`modulePricing.tierCodes.${code}`, code);
  const getPricingModelLabel = (model: PricingModel) => t(`modulePricing.tiers.pricingModels.${model}`);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterModule}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded-lg bg-surface focus:ring-2 focus:ring-primary/20"
          >
            <option value="">{t('common.allModules', 'All Modules')}</option>
            {moduleOptions.map(code => (
              <option key={code} value={code}>{getModuleName(code)}</option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg border border-border hover:bg-hover"
            title={t('common.refresh')}
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <Button onClick={openCreateForm}>
          <Plus className="w-4 h-4 mr-2" />
          {t('modulePricing.tiers.createTier')}
        </Button>
      </div>

      {/* Tiers Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.module')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.tier')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.pricingModel')}</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.monthlyPrice')}</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.perUserPrice')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.userLimits')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.status')}</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted uppercase">{t('modulePricing.tiers.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={8} />)
            ) : Object.entries(groupedTiers).length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted">
                  {t('modulePricing.tiers.noTiers')}
                </td>
              </tr>
            ) : (
              Object.entries(groupedTiers).map(([moduleCode, moduleTiers]) => (
                <React.Fragment key={moduleCode}>
                  {moduleTiers.map((tier, idx) => (
                    <tr key={tier.id} className="hover:bg-hover/50">
                      <td className="px-4 py-3">
                        {idx === 0 && (
                          <span className="font-medium text-foreground">{getModuleName(moduleCode)}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <span className="font-medium text-foreground">{tier.tierName}</span>
                          <span className="ml-2 text-xs text-muted">({getTierName(tier.tierCode)})</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          tier.pricingModel === 'PER_USER'
                            ? 'bg-blue-500/15 text-blue-600'
                            : 'bg-purple-500/15 text-purple-600'
                        }`}>
                          {getPricingModelLabel(tier.pricingModel)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {tier.pricingModel === 'FLAT' ? (
                          <span className="text-foreground">${tier.monthlyPriceUsd}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {tier.pricingModel === 'PER_USER' ? (
                          <span className="text-foreground">${tier.perUserMonthlyUsd}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {tier.minUsers} - {tier.maxUsers || t('modulePricing.unlimited')}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleActive(tier)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                            tier.isActive
                              ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                              : 'bg-gray-500/15 text-gray-500 hover:bg-gray-500/25'
                          }`}
                        >
                          {tier.isActive ? <ToggleRight className="w-3 h-3" /> : <ToggleLeft className="w-3 h-3" />}
                          {tier.isActive ? t('common.active') : t('common.inactive')}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditForm(tier)}
                            className="p-1.5 rounded-lg hover:bg-hover text-muted hover:text-foreground"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(tier.id)}
                            className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalElements > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted">
            {t('modulePricing.pagination.showing', {
              from: currentPage * pageSize + 1,
              to: Math.min((currentPage + 1) * pageSize, totalElements),
              total: totalElements
            })}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted">
              {t('modulePricing.pagination.page', { current: currentPage + 1, total: Math.max(1, totalPages) })}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border-subtle rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-4 border-b border-border-subtle flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {editingTier ? t('modulePricing.tiers.editTier') : t('modulePricing.tiers.createTier')}
              </h3>
              <button onClick={() => setIsFormOpen(false)} className="p-2 hover:bg-hover rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('modulePricing.tiers.form.moduleCode')} *
                  </label>
                  <select
                    value={formData.moduleCode}
                    onChange={(e) => setFormData({ ...formData, moduleCode: e.target.value })}
                    required
                    disabled={!!editingTier}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{t('common.selectOption', 'Select...')}</option>
                    {moduleCodes.map(code => (
                      <option key={code} value={code}>{t(`modulePricing.modules.${code}`, code)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('modulePricing.tiers.form.tierCode')} *
                  </label>
                  <select
                    value={formData.tierCode}
                    onChange={(e) => setFormData({ ...formData, tierCode: e.target.value })}
                    required
                    disabled={!!editingTier}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">{t('common.selectOption', 'Select...')}</option>
                    {TIER_CODES.map(code => (
                      <option key={code} value={code}>{t(`modulePricing.tierCodes.${code}`, code)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <FormInput
                label={t('modulePricing.tiers.form.tierName')}
                value={formData.tierName}
                onChange={(e) => setFormData({ ...formData, tierName: e.target.value })}
                required
                placeholder="HR Basic, CRM Professional..."
              />
              <FormInput
                label={t('modulePricing.tiers.form.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description of features included..."
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t('modulePricing.tiers.form.pricingModel')}
                </label>
                <select
                  value={formData.pricingModel}
                  onChange={(e) => setFormData({ ...formData, pricingModel: e.target.value as PricingModel })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-surface focus:ring-2 focus:ring-primary/20"
                  disabled={!!editingTier}
                >
                  <option value="FLAT">{t('modulePricing.tiers.pricingModels.FLAT')}</option>
                  <option value="PER_USER">{t('modulePricing.tiers.pricingModels.PER_USER')}</option>
                </select>
              </div>
              {formData.pricingModel === 'FLAT' ? (
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    label={t('modulePricing.tiers.form.monthlyPriceUsd')}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthlyPriceUsd}
                    onChange={(e) => setFormData({ ...formData, monthlyPriceUsd: parseFloat(e.target.value) || 0 })}
                  />
                  <FormInput
                    label={t('modulePricing.tiers.form.annualPriceUsd')}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.annualPriceUsd}
                    onChange={(e) => setFormData({ ...formData, annualPriceUsd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <FormInput
                    label={t('modulePricing.tiers.form.perUserMonthlyUsd')}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.perUserMonthlyUsd}
                    onChange={(e) => setFormData({ ...formData, perUserMonthlyUsd: parseFloat(e.target.value) || 0 })}
                  />
                  <FormInput
                    label={t('modulePricing.tiers.form.perUserAnnualUsd')}
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.perUserAnnualUsd}
                    onChange={(e) => setFormData({ ...formData, perUserAnnualUsd: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  label={t('modulePricing.tiers.form.minUsers')}
                  type="number"
                  min="1"
                  value={formData.minUsers}
                  onChange={(e) => setFormData({ ...formData, minUsers: parseInt(e.target.value) || 1 })}
                />
                <FormInput
                  label={t('modulePricing.tiers.form.maxUsers')}
                  type="number"
                  min="1"
                  value={formData.maxUsers || ''}
                  onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder={t('modulePricing.unlimited')}
                />
                <FormInput
                  label={t('modulePricing.tiers.form.trialDays')}
                  type="number"
                  min="0"
                  value={formData.trialDays}
                  onChange={(e) => setFormData({ ...formData, trialDays: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-foreground">{t('modulePricing.tiers.form.isActive')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                  />
                  <span className="text-sm text-foreground">{t('modulePricing.tiers.form.isPublic')}</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border-subtle">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                  {editingTier ? t('common.save') : t('common.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        title={t('common.confirmDelete')}
        message={t('modulePricing.tiers.actions.confirmDelete')}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

// ============================================================================
// Volume Discounts Tab
// ============================================================================

interface DiscountFormData {
  ruleName: string;
  description: string;
  minModules: number;
  maxModules: number | null;
  discountPercent: number;
  isActive: boolean;
  priority: number;
}

const defaultDiscountForm: DiscountFormData = {
  ruleName: '',
  description: '',
  minModules: 2,
  maxModules: null,
  discountPercent: 5,
  isActive: true,
  priority: 0,
};

const VolumeDiscountsTab: React.FC = () => {
  const { t } = useTranslation('platform');
  const { success: showSuccess, error: showError } = useNotification();

  const { data: discounts = [], isLoading, refetch } = useVolumeDiscounts();
  const createMutation = useCreateVolumeDiscount();
  const updateMutation = useUpdateVolumeDiscount();
  const deleteMutation = useDeleteVolumeDiscount();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<VolumeDiscountRule | null>(null);
  const [formData, setFormData] = useState<DiscountFormData>(defaultDiscountForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Sort discounts by priority
  const sortedDiscounts = useMemo(() => {
    return [...discounts].sort((a, b) => b.priority - a.priority);
  }, [discounts]);

  const openCreateForm = () => {
    setFormData(defaultDiscountForm);
    setEditingDiscount(null);
    setIsFormOpen(true);
  };

  const openEditForm = (discount: VolumeDiscountRule) => {
    setFormData({
      ruleName: discount.ruleName,
      description: discount.description || '',
      minModules: discount.minModules,
      maxModules: discount.maxModules || null,
      discountPercent: discount.discountPercent,
      isActive: discount.isActive,
      priority: discount.priority,
    });
    setEditingDiscount(discount);
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data: CreateVolumeDiscountRequest = {
        ruleName: formData.ruleName,
        description: formData.description || undefined,
        minModules: formData.minModules,
        maxModules: formData.maxModules || undefined,
        discountPercent: formData.discountPercent,
        isActive: formData.isActive,
        priority: formData.priority,
      };
      if (editingDiscount) {
        await updateMutation.mutateAsync({ id: editingDiscount.id, data });
        showSuccess(t('modulePricing.discounts.messages.updated'));
      } else {
        await createMutation.mutateAsync(data);
        showSuccess(t('modulePricing.discounts.messages.created'));
      }
      setIsFormOpen(false);
      setEditingDiscount(null);
    } catch (err) {
      const fallback = editingDiscount ? 'Failed to update discount' : 'Failed to create discount';
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: fallback };
      showError(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      showSuccess(t('modulePricing.discounts.messages.deleted'));
      setDeleteConfirm(null);
    } catch (err) {
      const { message } = isApiError(err) ? formatErrorForDisplay(err, t) : { message: 'Failed to delete discount' };
      showError(message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg border border-border hover:bg-hover"
          title={t('common.refresh')}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
        <Button onClick={openCreateForm}>
          <Plus className="w-4 h-4 mr-2" />
          {t('modulePricing.discounts.createDiscount')}
        </Button>
      </div>

      {/* Discounts Table */}
      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.ruleName')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.moduleRange')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.discountPercent')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.priority')}</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.status')}</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted uppercase">{t('modulePricing.discounts.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} cols={6} />)
            ) : sortedDiscounts.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted">
                  {t('modulePricing.discounts.noDiscounts')}
                </td>
              </tr>
            ) : (
              sortedDiscounts.map((discount) => (
                <tr key={discount.id} className="hover:bg-hover/50">
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-medium text-foreground">{discount.ruleName}</span>
                      {discount.description && (
                        <p className="text-xs text-muted mt-0.5">{discount.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/15 text-blue-600">
                      {discount.minModules} - {discount.maxModules || '∞'} {t('modulePricing.modules_count', { count: discount.minModules })}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 font-mono text-lg font-bold text-emerald-600">
                      <Percent className="w-4 h-4" />
                      {discount.discountPercent}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1 text-sm text-muted">
                      {discount.priority > 0 ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {discount.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      discount.isActive
                        ? 'bg-emerald-500/15 text-emerald-600'
                        : 'bg-gray-500/15 text-gray-500'
                    }`}>
                      {discount.isActive ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {discount.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditForm(discount)}
                        className="p-1.5 rounded-lg hover:bg-hover text-muted hover:text-foreground"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(discount.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface border border-border-subtle rounded-xl w-full max-w-lg m-4">
            <div className="p-4 border-b border-border-subtle flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {editingDiscount ? t('modulePricing.discounts.editDiscount') : t('modulePricing.discounts.createDiscount')}
              </h3>
              <button onClick={() => setIsFormOpen(false)} className="p-2 hover:bg-hover rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <FormInput
                label={t('modulePricing.discounts.form.ruleName')}
                value={formData.ruleName}
                onChange={(e) => setFormData({ ...formData, ruleName: e.target.value })}
                required
                placeholder="2 Modules Bundle"
              />
              <FormInput
                label={t('modulePricing.discounts.form.description')}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="5% off when subscribing to 2 modules"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label={t('modulePricing.discounts.form.minModules')}
                  type="number"
                  min="1"
                  value={formData.minModules}
                  onChange={(e) => setFormData({ ...formData, minModules: parseInt(e.target.value) || 1 })}
                  required
                />
                <FormInput
                  label={t('modulePricing.discounts.form.maxModules')}
                  type="number"
                  min="1"
                  value={formData.maxModules || ''}
                  onChange={(e) => setFormData({ ...formData, maxModules: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder={t('modulePricing.unlimited')}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label={t('modulePricing.discounts.form.discountPercent')}
                  type="number"
                  min="0.01"
                  max="100"
                  step="0.01"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData({ ...formData, discountPercent: parseFloat(e.target.value) || 0 })}
                  required
                />
                <FormInput
                  label={t('modulePricing.discounts.form.priority')}
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary/20"
                />
                <span className="text-sm text-foreground">{t('modulePricing.discounts.form.isActive')}</span>
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-border-subtle">
                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
                  {editingDiscount ? t('common.save') : t('common.create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={!!deleteConfirm}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        title={t('common.confirmDelete')}
        message={t('modulePricing.discounts.actions.confirmDelete')}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};

// ============================================================================
// Analytics Tab
// ============================================================================

const AnalyticsTab: React.FC = () => {
  const { t } = useTranslation('platform');

  const { data: analytics, isLoading: analyticsLoading } = useSubscriptionAnalytics();
  const { data: mrr, isLoading: mrrLoading } = useTotalMrr();

  const isLoading = analyticsLoading || mrrLoading;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
        ) : (
          <>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <div className="text-xs text-muted">{t('modulePricing.analytics.metrics.totalMrr')}</div>
                  <div className="font-mono text-2xl font-bold text-foreground">
                    ${(mrr || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-xs text-muted">{t('modulePricing.analytics.metrics.totalSubscriptions')}</div>
                  <div className="font-mono text-2xl font-bold text-foreground">
                    {analytics?.totalSubscriptions || 0}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/15 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <div className="text-xs text-muted">{t('modulePricing.analytics.metrics.activeSubscriptions')}</div>
                  <div className="font-mono text-2xl font-bold text-foreground">
                    {analytics?.activeSubscriptions || 0}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-surface border border-border-subtle rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <div className="text-xs text-muted">{t('modulePricing.analytics.metrics.trialSubscriptions')}</div>
                  <div className="font-mono text-2xl font-bold text-foreground">
                    {analytics?.trialSubscriptions || 0}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Module Popularity & Tier Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Module Popularity */}
        <div className="bg-surface border border-border-subtle rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            {t('modulePricing.analytics.modulePopularity')}
          </h3>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 w-full bg-muted/20 rounded"></div>
                </div>
              ))}
            </div>
          ) : analytics?.modulePopularity && Object.keys(analytics.modulePopularity).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.modulePopularity)
                .sort(([, a], [, b]) => b - a)
                .map(([module, count]) => {
                  const maxCount = Math.max(...Object.values(analytics.modulePopularity || {}));
                  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={module}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-foreground">{t(`modulePricing.modules.${module}`, module)}</span>
                        <span className="font-mono text-muted">{count}</span>
                      </div>
                      <div className="h-2 bg-muted/20 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-sm text-muted">{t('common.noData', 'No data available')}</p>
          )}
        </div>

        {/* Tier Distribution */}
        <div className="bg-surface border border-border-subtle rounded-xl p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            {t('modulePricing.analytics.tierDistribution')}
          </h3>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 w-full bg-muted/20 rounded"></div>
                </div>
              ))}
            </div>
          ) : analytics?.tierDistribution && Object.keys(analytics.tierDistribution).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(analytics.tierDistribution)
                .sort(([, a], [, b]) => b - a)
                .map(([tier, count]) => {
                  const total = Object.values(analytics.tierDistribution || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  const tierColors: Record<string, string> = {
                    BASIC: 'bg-gray-500',
                    PRO: 'bg-purple-500',
                    ENTERPRISE: 'bg-primary',
                  };
                  return (
                    <div key={tier}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-foreground">{t(`modulePricing.tierCodes.${tier}`, tier)}</span>
                        <span className="font-mono text-muted">{count} ({percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 bg-muted/20 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${tierColors[tier] || 'bg-primary'} rounded-full transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-sm text-muted">{t('common.noData', 'No data available')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

type TabType = 'tiers' | 'discounts' | 'analytics';

export default function ModulePricingPage() {
  const { t } = useTranslation('platform');
  const [activeTab, setActiveTab] = useState<TabType>('tiers');

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'tiers', label: t('modulePricing.tabs.tiers'), icon: <Layers className="w-4 h-4" /> },
    { id: 'discounts', label: t('modulePricing.tabs.discounts'), icon: <Percent className="w-4 h-4" /> },
    { id: 'analytics', label: t('modulePricing.tabs.analytics'), icon: <BarChart3 className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <DollarSign className="w-6 h-6" /> {t('modulePricing.title')}
        </h1>
        <p className="text-sm text-muted">{t('modulePricing.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-subtle">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-foreground'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'tiers' && <PricingTiersTab />}
      {activeTab === 'discounts' && <VolumeDiscountsTab />}
      {activeTab === 'analytics' && <AnalyticsTab />}
    </div>
  );
}
